import { useMemo } from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { WellnessMetricDto } from '@repo/shared/dtos/analytics/WellnessDto';
import { CHART_CONFIG, CHART_THEMES } from '../config';
import { useResponsiveChartConfig } from '../shared/useResponsiveChartConfig';
import { transformWellnessMetrics, getImprovementColor } from './wellnessUtils';
import { WellnessChartTooltip } from './WellnessChartTooltip';

interface WellnessPrePostBarChartProps {
  data: WellnessMetricDto | null;
  height?: number;
}

export function WellnessPrePostBarChart({ data, height = 380 }: WellnessPrePostBarChartProps) {
  const { getMargins } = useResponsiveChartConfig();
  const chartData = useMemo(() => transformWellnessMetrics(data), [data]);

  const hasData = useMemo(() => {
    return chartData.some(item => item.pre !== null || item.post !== null);
  }, [chartData]);

  const barData = useMemo(
    () =>
      chartData.map(item => ({
        metric: item.metric,
        id: item.id,
        pre: item.pre ?? 0,
        post: item.post ?? 0,
        ...(item.improvementPercentage !== null && {
          improvementPercentage: item.improvementPercentage,
        }),
      })),
    [chartData],
  );

  if (!hasData) {
    return (
      <div className="flex h-full items-center justify-center text-gray-500" style={{ height }}>
        <p className="text-sm">No wellness metrics data available</p>
      </div>
    );
  }

  return (
    <div style={{ height }}>
      <ResponsiveBar
        data={barData}
        keys={['pre', 'post']}
        indexBy="metric"
        margin={getMargins('BAR_CHART')}
        padding={0.4}
        groupMode="grouped"
        valueScale={{ type: 'linear', min: 0, max: 1 }}
        indexScale={{ type: 'band', round: true }}
        colors={[CHART_CONFIG.WELLNESS_CHART_COLORS.PRE, CHART_CONFIG.WELLNESS_CHART_COLORS.POST]}
        borderColor={{
          from: 'color',
          modifiers: [['darker', 0.3]],
        }}
        borderWidth={1}
        axisTop={null}
        axisRight={null}
        axisBottom={{
          tickSize: CHART_CONFIG.TICK_SIZE,
          tickPadding: CHART_CONFIG.TICK_PADDING,
          tickRotation: -35,
          format: value => String(value).replace(/\n/g, ' '),
        }}
        axisLeft={{
          tickSize: CHART_CONFIG.TICK_SIZE,
          tickPadding: CHART_CONFIG.TICK_PADDING,
          tickRotation: 0,
          legend: 'Average Score',
          legendPosition: 'middle',
          legendOffset: -55,
          tickValues: [0, 0.2, 0.4, 0.6, 0.8, 1.0],
          format: value => value.toFixed(1),
        }}
        labelSkipWidth={20}
        labelSkipHeight={20}
        labelTextColor="#ffffff"
        enableLabel={true}
        label={d => {
          const source = chartData.find(item => item.metric === d.indexValue);
          const rawValue = d.id === 'pre' ? source?.pre : source?.post;
          return rawValue !== null && rawValue !== undefined ? (d.value as number).toFixed(2) : '';
        }}
        legends={[
          {
            dataFrom: 'keys',
            anchor: 'top-right',
            direction: 'row',
            justify: false,
            translateX: -10,
            translateY: -25,
            itemsSpacing: 24,
            itemWidth: 60,
            itemHeight: 20,
            itemDirection: 'left-to-right',
            itemOpacity: 0.9,
            symbolSize: 14,
            itemTextColor: '#e5e7eb',
            effects: [
              {
                on: 'hover',
                style: {
                  itemOpacity: 1,
                  itemTextColor: '#e5e7eb',
                },
              },
            ],
          },
        ]}
        tooltip={({ indexValue, data }) => (
          <WellnessChartTooltip
            indexValue={indexValue}
            data={data}
            getImprovementColor={getImprovementColor}
          />
        )}
        animate={true}
        theme={CHART_THEMES.default}
      />
    </div>
  );
}
